import { nextTick, ref } from 'vue';

/**
 * Composable for handling file drag and drop functionality
 * @param {Function} onFileDrop - Callback function that receives an array of dropped files
 * @param {Object} options - Configuration options
 * @param {boolean} options.multiple - Whether to allow multiple file drops (default: true)
 * @param {Array<string>} options.accept - Array of accepted file types/extensions
 * @param {number} options.maxSize - Maximum file size in bytes
 * @returns {Object} - Drop zone reactive state and setup function
 */
export function useFileDropZone(onFileDrop, options = {}) {
  const { multiple = true, accept = [], maxSize = null } = options;

  const dropZoneRef = ref(null);
  const isOverDropZone = ref(false);
  const isDragging = ref(false);

  /**
   * Validates dropped files against the provided options
   * @param {FileList} files - Files to validate
   * @returns {Array} - Array of valid files
   */
  const validateFiles = (files) => {
    const validFiles = [];
    const errors = [];

    for (const file of files) {
      // Check if multiple files are allowed
      if (!multiple && validFiles.length >= 1) {
        errors.push(`Only one file is allowed`);
        break;
      }

      // Check file type if accept array is provided
      if (accept.length > 0) {
        const isValidType = accept.some((type) => {
          if (type.startsWith('.')) {
            // Extension check
            return file.name.toLowerCase().endsWith(type.toLowerCase());
          } else {
            // MIME type check
            return file.type.startsWith(type);
          }
        });

        if (!isValidType) {
          errors.push(`File "${file.name}" is not an accepted file type`);
          continue;
        }
      }

      // Check file size if maxSize is provided
      if (maxSize && file.size > maxSize) {
        errors.push(`File "${file.name}" exceeds maximum size of ${formatFileSize(maxSize)}`);
        continue;
      }

      validFiles.push(file);
    }

    if (errors.length > 0) {
      console.warn('File validation errors:', errors);
    }

    return validFiles;
  };

  /**
   * Format file size in human readable format
   * @param {number} bytes - File size in bytes
   * @returns {string} - Formatted file size
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Check if the drag event contains files (external files, not internal draggable elements)
   * @param {DragEvent} e - Drag event
   * @returns {boolean} - True if the event contains files
   */
  const hasFiles = (e) => {
    return e.dataTransfer && e.dataTransfer.types.includes('Files');
  };

  /**
   * Handle drag enter event
   * @param {DragEvent} e - Drag event
   */
  const handleDragEnter = (e) => {
    e.preventDefault();

    // Only show drop zone for external files (not internal draggable elements)
    if (hasFiles(e)) {
      isOverDropZone.value = true;
      isDragging.value = true;
    }
  };

  /**
   * Handle drag over event
   * @param {DragEvent} e - Drag event
   */
  const handleDragOver = (e) => {
    e.preventDefault();

    // Only allow drop for external files
    if (hasFiles(e)) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  /**
   * Handle drag leave event
   * @param {DragEvent} e - Drag event
   */
  const handleDragLeave = (e) => {
    e.preventDefault();

    // Only hide if we're leaving the drop zone container itself
    if (dropZoneRef.value && !dropZoneRef.value.contains(e.relatedTarget)) {
      isOverDropZone.value = false;
      isDragging.value = false;
    }
  };

  /**
   * Handle drop event
   * @param {DragEvent} e - Drag event
   */
  const handleDrop = (e) => {
    e.preventDefault();
    isOverDropZone.value = false;
    isDragging.value = false;

    // Only handle file drops
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      const validFiles = validateFiles(files);

      if (validFiles.length > 0 && onFileDrop) {
        onFileDrop(validFiles);
      }
    }
  };

  /**
   * Setup the drop zone by attaching event listeners
   * @returns {Function} - Cleanup function to remove event listeners
   */
  const setupDropZone = async () => {
    // Wait for the next tick to ensure the ref is available
    await nextTick();

    if (!dropZoneRef.value) {
      console.warn('Drop zone ref is not available. Make sure to set the ref on your drop zone element.');
      return () => {};
    }

    const element = dropZoneRef.value;

    // Attach event listeners
    element.addEventListener('dragenter', handleDragEnter);
    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('drop', handleDrop);

    // Return cleanup function
    return () => {
      if (element) {
        element.removeEventListener('dragenter', handleDragEnter);
        element.removeEventListener('dragover', handleDragOver);
        element.removeEventListener('dragleave', handleDragLeave);
        element.removeEventListener('drop', handleDrop);
      }
    };
  };

  /**
   * Manually trigger file selection (for click-to-upload functionality)
   * @param {Object} inputOptions - Options for the file input
   */
  const openFileDialog = (inputOptions = {}) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = multiple;

    if (accept.length > 0) {
      input.accept = accept.join(',');
    }

    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      const validFiles = validateFiles(files);

      if (validFiles.length > 0 && onFileDrop) {
        onFileDrop(validFiles);
      }
    };

    input.click();
  };

  return {
    // Refs
    dropZoneRef,
    isOverDropZone,
    isDragging,

    // Methods
    setupDropZone,
    openFileDialog,
    validateFiles,

    // Utilities
    formatFileSize,
  };
}
